import { HttpErrorResponse } from '@angular/common/http';
import type { ErrorEvent, EventHint } from '@sentry/core';

/**
 * Builds a Sentry-friendly `{ type, value }` exception pair from an Angular
 * `HttpErrorResponse`.
 *
 * @remarks
 * **Why this exists (2026-08-05):** live Sentry events kept showing up as
 * `"Object captured as exception with keys: error, headers, message, name,
 * ok, redirected, responseType, status, statusText, type, url"` — Sentry's
 * generic non-Error-object fallback (`getNonErrorObjectExceptionValue()` in
 * `@sentry/browser`'s `eventbuilder.js`) — whenever a raw `HttpErrorResponse`
 * reached Sentry. Root cause: `HttpErrorResponse` (`@angular/common/http`)
 * is declared `class HttpErrorResponse extends HttpResponseBase implements
 * Error` — it only structurally *implements* the `Error` interface (has
 * `.message`/`.name`), it does not *extend* the real `Error` class, so
 * every one of Sentry's own "is this actually an Error?" checks
 * (`isError()`, `instanceof Error`, `Object.prototype.toString.call(...)
 * === '[object Error]'`) fails for it, and Sentry treats it as a plain
 * object — enumerating its own keys instead of building a real
 * message/stack.
 *
 * **This single formatter is shared by two independent fix sites**, because
 * a raw `HttpErrorResponse` can reach Sentry through more than one path and
 * each needed its own fix:
 * - `SentryService.captureException()` — normalizes any `HttpErrorResponse`
 *   passed to an *explicit* `captureException()` call (e.g.
 *   `globalErrorHandlerInterceptor`, `AppSettingsService.fetchAndCache()`).
 * - `main.ts`'s Sentry `beforeSend` hook — the backstop for an
 *   `HttpErrorResponse` that reaches Sentry via an *automatic* capture path
 *   instead: `@sentry/browser`'s `GlobalHandlers` integration captures an
 *   uncaught `window.onerror` or an unhandled promise rejection directly,
 *   with no Angular-aware extraction step at all (confirmed by reading
 *   `globalhandlers.js` — a non-primitive rejection reason goes straight to
 *   `eventFromUnknownInput()`, the same generic fallback). Neither
 *   `SentryService` nor Angular's own `ErrorHandler` (`createErrorHandler()`,
 *   whose `_defaultExtractor()` *does* already special-case
 *   `HttpErrorResponse` — see its own `extractHttpModuleError()`) is ever
 *   consulted for this path, so `SentryService`'s normalization alone cannot
 *   cover it — only a `beforeSend` hook, which every event passes through
 *   regardless of how it was captured, can.
 *
 * @param err - The `HttpErrorResponse` to describe.
 * @returns `{ type, value }` — a real exception type/message pair, plus the
 *          original status/url so the grouping is still meaningful.
 */
export function describeHttpErrorResponse(err: HttpErrorResponse): { type: string; value: string } {
  return {
    type: 'HttpErrorResponse',
    value: `HTTP ${err.status} ${err.statusText} — ${err.url ?? 'unknown URL'}: ${err.message}`,
  };
}

/**
 * Sentry `beforeSend` hook — rewrites a raw `HttpErrorResponse` event into
 * one with a real, groupable exception `type`/`value` instead of Sentry's
 * generic "Object captured as exception with keys: ..." fallback.
 *
 * @remarks
 * Wired into `SentryCapacitor.init()` in `main.ts` (`beforeSend:
 * normalizeHttpErrorResponseEvents`) rather than kept inline there, so it can
 * be unit-tested directly — `main.ts` itself is a bootstrap IIFE with no
 * exports to test against. See {@link describeHttpErrorResponse}'s doc
 * comment for why this specific hook (as opposed to `SentryService`'s own
 * normalization) is needed: it is the only place every Sentry event passes
 * through regardless of which capture path produced it, which matters
 * because `@sentry/browser`'s `GlobalHandlers` integration (active by
 * default — confirmed `@sentry/core`'s `getIntegrationsToSetup()` merges a
 * user-supplied `integrations` array onto the defaults rather than replacing
 * them, so this app's `integrations: []` does not disable it) captures an
 * uncaught `window.onerror` or an unhandled promise rejection by handing the
 * raw value straight to `eventFromUnknownInput()`, with no Angular-aware
 * extraction step at all.
 *
 * Only the first exception entry's `type`/`value` are overwritten — any
 * stacktrace/mechanism data Sentry already built for the event is left
 * untouched. A non-`HttpErrorResponse` event (including one already
 * normalized by `SentryService`, where `hint.originalException` is by then a
 * plain `Error`) passes through unmodified.
 *
 * @param event - The event Sentry is about to send.
 * @param hint  - Capture hint; `originalException` is the raw captured value.
 * @returns The (possibly rewritten) event.
 */
export function normalizeHttpErrorResponseEvents(event: ErrorEvent, hint: EventHint): ErrorEvent {
  const original = hint.originalException;
  const firstException = event.exception?.values?.[0];
  if (original instanceof HttpErrorResponse && firstException) {
    const { type, value } = describeHttpErrorResponse(original);
    firstException.type = type;
    firstException.value = value;
  }
  return event;
}
